import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const strip = { background: "linear-gradient(90deg, #8c6d3f 0%, #e5c158 50%, #8c6d3f 100%)" };
const goldBg = { background: "linear-gradient(135deg, #a87f24 0%, #e5c158 50%, #d4af37 100%)" };

const CARDS = [
  {
    icon: MapPin,
    title: "Visit Us",
    lines: ["11111 Katy Freeway, Suite 910", "Houston, TX 77079"],
    href: "https://maps.google.com/?q=11111+Katy+Freeway+Suite+910+Houston+TX+77079",
    action: "Get Directions",
    testid: "contact-card-address",
  },
  {
    icon: Phone,
    title: "Call Us",
    lines: ["888-708-0195"],
    href: "tel:8887080195",
    action: "Call Now",
    testid: "contact-card-phone",
  },
  {
    icon: Mail,
    title: "Email Us",
    lines: ["info@emisonmanagement.com"],
    href: "mailto:info@emisonmanagement.com",
    action: "Send Email",
    testid: "contact-card-email",
  },
];

const Contact = () => (
  <main data-testid="contact-page" className="pt-20">
    <section className="py-20 lg:py-28 px-6 lg:px-12">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <p className="flex items-center gap-3 text-[10px] font-mono-accent uppercase tracking-[0.4em] text-gold mb-6">
            <span className="w-1.5 h-1.5 rotate-45 bg-[#c9a227]" />
            Contact Emison Management
          </p>
          <h1 className="font-serif font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#f3e5c8] leading-[1.08] max-w-3xl">
            Let&rsquo;s talk about <span className="italic gold-text">your next move.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#cbb493] leading-relaxed max-w-2xl">
            Whether you&rsquo;re an artist weighing your first advance or a label planning
            a signing season — our team answers within one business day.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1}>
              <div
                data-testid={c.testid}
                className="group relative bg-[#fffdf9] border border-[#e3d3ac] p-8 lg:p-10 shadow-[0_20px_60px_rgba(45,26,11,0.15)] h-full transition-all duration-700 hover:-translate-y-1.5 hover:shadow-[0_35px_80px_rgba(201,162,39,0.3)]"
              >
                <span className="absolute bottom-0 left-0 right-0 h-[3px]" style={strip} />
                <span
                  className="w-11 h-11 rotate-45 flex items-center justify-center mb-8 shadow-[0_8px_20px_rgba(201,162,39,0.4)] transition-transform duration-700 group-hover:rotate-[135deg]"
                  style={goldBg}
                >
                  <c.icon className="-rotate-45 w-5 h-5 text-white transition-transform duration-700 group-hover:-rotate-[135deg]" />
                </span>
                <h2 className="font-serif text-2xl text-[#4D4232]">{c.title}</h2>
                {c.lines.map((l) => (
                  <p key={l} className="mt-3 text-sm text-sand leading-relaxed">{l}</p>
                ))}
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  data-testid={`${c.testid}-action`}
                  className="group/a mt-7 inline-flex items-center gap-3 text-[11px] font-mono-accent uppercase tracking-[0.25em] text-champagne"
                >
                  <span className="border-b border-[#c9a227]/50 pb-1 group-hover/a:border-[#c9a227] group-hover/a:text-gold transition-colors">
                    {c.action}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5 group-hover/a:translate-x-1 transition-transform" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15} className="mt-16 text-center">
          <p className="text-sm text-[#cbb493] mb-8">
            Ready to skip the line? Begin your application and get an answer within 24 hours.
          </p>
          <Link
            to="/apply"
            data-testid="contact-cta-apply"
            className="sheen-btn inline-flex items-center gap-3 text-[#4D4232] text-[11px] font-semibold font-mono-accent uppercase tracking-[0.25em] px-10 py-5 shadow-[0_10px_25px_rgba(201,162,39,0.4)] hover:scale-[1.03] transition-transform"
            style={goldBg}
          >
            Begin Application <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Contact;
